import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { messagesAPI, leadsAPI, landingPagesAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Clock, Calendar, Users, Send, Trash2, Loader2, 
  Edit, Eye, Plus, Mail, Repeat, CheckCircle, XCircle, Youtube, Link2
} from 'lucide-react';
import { toast } from 'sonner';

const ScheduledMessages = () => {
  const { user } = useAuth();
  const [scheduledMessages, setScheduledMessages] = useState([]);
  const [leads, setLeads] = useState([]);
  const [pages, setPages] = useState([]);
  const [timezones, setTimezones] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Create/Edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Preview dialog
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    subject: '',
    content: '',
    recipient_type: 'individual',
    lead_id: '',
    landing_page_id: '',
    schedule_type: 'one_time',
    scheduled_date: '',
    scheduled_time: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    recurring_frequency: 'weekly',
    recurring_end_date: '',
    youtube_url: '',
    cta_link: '',
    cta_text: ''
  });

  const isPaidPlan = user?.plan === 'pro' || user?.plan === 'power';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [scheduledRes, leadsRes, pagesRes, tzRes] = await Promise.all([
        messagesAPI.getScheduled(),
        leadsAPI.getAll(),
        landingPagesAPI.getAll(),
        messagesAPI.getTimezones()
      ]);
      setScheduledMessages(scheduledRes.data.scheduled_messages || []);
      setLeads(leadsRes.data.leads || []);
      setPages(pagesRes.data.landing_pages || []);
      setTimezones(tzRes.data.timezones || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingMessage(null);
    setFormData({
      subject: '',
      content: '',
      recipient_type: 'individual',
      lead_id: '',
      landing_page_id: '',
      schedule_type: 'one_time',
      scheduled_date: '',
      scheduled_time: '09:00',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      recurring_frequency: 'weekly',
      recurring_end_date: '',
      youtube_url: '',
      cta_link: '',
      cta_text: ''
    });
    setDialogOpen(true);
  };

  const openEditDialog = (msg) => {
    setEditingMessage(msg);
    const scheduledDt = new Date(msg.scheduled_datetime);
    setFormData({
      subject: msg.subject || '',
      content: msg.content,
      recipient_type: msg.recipient_type,
      lead_id: msg.lead_id || '',
      landing_page_id: msg.landing_page_id || '',
      schedule_type: msg.schedule_type,
      scheduled_date: scheduledDt.toISOString().split('T')[0],
      scheduled_time: scheduledDt.toTimeString().slice(0, 5),
      timezone: msg.timezone || 'UTC',
      recurring_frequency: msg.recurring_frequency || 'weekly',
      recurring_end_date: msg.recurring_end_date ? msg.recurring_end_date.split('T')[0] : '',
      youtube_url: msg.youtube_url || '',
      cta_link: msg.cta_link || '',
      cta_text: msg.cta_text || ''
    });
    setDialogOpen(true);
  };

  const openPreviewDialog = async (msg) => {
    setPreviewLoading(true);
    setPreviewDialogOpen(true);
    try {
      const response = await messagesAPI.previewScheduled(msg.id);
      setPreviewData(response.data);
    } catch (error) {
      toast.error('Failed to load preview');
      setPreviewDialogOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isPaidPlan) {
      toast.error('Scheduled messages require Pro or Power plan');
      return;
    }
    
    if (!formData.content.trim()) {
      toast.error('Message content is required');
      return;
    }
    
    if (!formData.scheduled_date || !formData.scheduled_time) {
      toast.error('Please select a date and time');
      return;
    }
    
    if (formData.recipient_type === 'individual' && !formData.lead_id) {
      toast.error('Please select a lead');
      return;
    }
    
    if (formData.recipient_type === 'capture_page' && !formData.landing_page_id) {
      toast.error('Please select a capture page');
      return;
    }

    setSubmitting(true);
    try {
      const scheduled_datetime = `${formData.scheduled_date}T${formData.scheduled_time}:00`;
      
      const payload = {
        subject: formData.subject,
        content: formData.content,
        recipient_type: formData.recipient_type,
        lead_id: formData.recipient_type === 'individual' ? formData.lead_id : null,
        landing_page_id: formData.recipient_type === 'capture_page' ? formData.landing_page_id : null,
        schedule_type: formData.schedule_type,
        scheduled_datetime,
        timezone: formData.timezone,
        recurring_frequency: formData.schedule_type === 'recurring' ? formData.recurring_frequency : null,
        recurring_end_date: formData.schedule_type === 'recurring' && formData.recurring_end_date 
          ? `${formData.recurring_end_date}T23:59:59` : null,
        youtube_url: formData.youtube_url || null,
        cta_link: formData.cta_link || null,
        cta_text: formData.cta_text || null
      };

      if (editingMessage) {
        await messagesAPI.updateScheduled(editingMessage.id, payload);
        toast.success('Scheduled message updated');
      } else {
        await messagesAPI.createScheduled(payload);
        toast.success('Message scheduled successfully');
      }
      
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule message');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteScheduled = async (id) => {
    if (!window.confirm('Delete this scheduled message?')) return;
    
    try {
      await messagesAPI.deleteScheduled(id);
      toast.success('Scheduled message deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete scheduled message');
    }
  };

  const getStatusBadge = (msg) => {
    const statusStyles = {
      scheduled: 'bg-yellow-500/20 text-yellow-400',
      recurring: 'bg-blue-500/20 text-blue-400',
      sent: 'bg-green-500/20 text-green-400',
      completed: 'bg-gray-500/20 text-gray-400',
      cancelled: 'bg-red-500/20 text-red-400'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusStyles[msg.status] || statusStyles.scheduled}`}>
        {msg.status.charAt(0).toUpperCase() + msg.status.slice(1)}
      </span>
    );
  };

  const getRecipientLabel = (msg) => {
    if (msg.recipient_type === 'individual' && msg.recipient_info) {
      return msg.recipient_info.name || msg.recipient_info.email;
    } else if (msg.recipient_type === 'capture_page' && msg.recipient_info) {
      return `${msg.recipient_info.title} (${msg.recipient_info.lead_count} leads)`;
    } else if (msg.recipient_type === 'all' && msg.recipient_info) {
      return `All Leads (${msg.recipient_info.lead_count})`;
    }
    return 'Unknown';
  };

  const formatScheduledTime = (datetime, tz) => {
    try {
      const date = new Date(datetime);
      return date.toLocaleString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }) + ` (${tz})`;
    } catch {
      return datetime;
    }
  };

  if (!isPaidPlan) {
    return (
      <div className="space-y-6" data-testid="scheduled-messages-page">
        <Card className="bg-void-paper border-white/10">
          <CardContent className="p-12 text-center">
            <Clock className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Scheduled Messages</h2>
            <p className="text-gray-400 mb-6">
              Schedule messages to your leads with our Pro or Power plan.
            </p>
            <Button 
              onClick={() => window.location.href = '/dashboard/billing'}
              className="bg-gradient-to-r from-neon-mint to-neon-violet"
            >
              Upgrade Now
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-neon-mint border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="scheduled-messages-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl text-white flex items-center gap-2">
            <Clock className="w-7 h-7 text-neon-mint" />
            Scheduled Messages
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            Schedule and automate messages to your leads
          </p>
        </div>
        <Button 
          onClick={openCreateDialog}
          className="bg-gradient-to-r from-neon-mint to-neon-violet"
          data-testid="create-scheduled-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Schedule Message
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="bg-void-paper border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-500" />
              </div>
              <div>
                <p className="text-gray-400 text-xs">Pending</p>
                <p className="text-white font-bold text-lg">
                  {scheduledMessages.filter(m => m.status === 'scheduled').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-void-paper border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Repeat className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-gray-400 text-xs">Recurring</p>
                <p className="text-white font-bold text-lg">
                  {scheduledMessages.filter(m => m.status === 'recurring').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-void-paper border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-gray-400 text-xs">Sent</p>
                <p className="text-white font-bold text-lg">
                  {scheduledMessages.filter(m => m.status === 'sent' || m.status === 'completed').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-void-paper border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-neon-mint/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-neon-mint" />
              </div>
              <div>
                <p className="text-gray-400 text-xs">Total Leads</p>
                <p className="text-white font-bold text-lg">{leads.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Scheduled Messages List */}
      <Card className="bg-void-paper border-white/10">
        <CardHeader>
          <CardTitle className="text-white font-heading">Your Scheduled Messages</CardTitle>
        </CardHeader>
        <CardContent>
          {scheduledMessages.length === 0 ? (
            <div className="text-center py-12">
              <Mail className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500">No scheduled messages yet</p>
              <p className="text-gray-600 text-sm mt-1">Click "Schedule Message" to create one</p>
            </div>
          ) : (
            <div className="space-y-4">
              {scheduledMessages.map((msg) => (
                <div 
                  key={msg.id}
                  className={`p-4 rounded-lg border ${
                    msg.status === 'scheduled' ? 'bg-yellow-500/5 border-yellow-500/30' :
                    msg.status === 'recurring' ? 'bg-blue-500/5 border-blue-500/30' :
                    msg.status === 'sent' ? 'bg-green-500/5 border-green-500/30' :
                    'bg-gray-500/5 border-gray-500/30'
                  }`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-white font-medium truncate">
                          {msg.subject || 'No subject'}
                        </h3>
                        {getStatusBadge(msg)}
                        {msg.schedule_type === 'recurring' && (
                          <span className="px-2 py-0.5 rounded-full text-xs bg-blue-500/20 text-blue-400 flex items-center gap-1">
                            <Repeat className="w-3 h-3" />
                            {msg.recurring_frequency}
                          </span>
                        )}
                      </div>
                      <p className="text-gray-400 text-sm mb-1 flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        To: {getRecipientLabel(msg)}
                      </p>
                      <p className="text-gray-500 text-sm flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {formatScheduledTime(msg.scheduled_datetime, msg.timezone)}
                      </p>
                      {msg.send_count > 0 && (
                        <p className="text-neon-mint text-xs mt-1">
                          Sent {msg.send_count} time{msg.send_count > 1 ? 's' : ''}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => openPreviewDialog(msg)}
                        size="sm"
                        variant="outline"
                        className="border-white/20 text-white hover:bg-white/10"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Preview
                      </Button>
                      {(msg.status === 'scheduled' || msg.status === 'recurring') && (
                        <>
                          <Button
                            onClick={() => openEditDialog(msg)}
                            size="sm"
                            variant="outline"
                            className="border-neon-mint/50 text-neon-mint hover:bg-neon-mint/10"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={() => deleteScheduled(msg.id)}
                            size="sm"
                            variant="outline"
                            className="border-red-500/50 text-red-500 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-void-paper border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingMessage ? 'Edit Scheduled Message' : 'Schedule a Message'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Recipients */}
            <div className="space-y-4">
              <Label className="text-white font-medium">Send To</Label>
              <Tabs 
                value={formData.recipient_type} 
                onValueChange={(v) => setFormData({...formData, recipient_type: v})}
              >
                <TabsList className="bg-zinc-900/50 w-full">
                  <TabsTrigger value="individual" className="flex-1 data-[state=active]:bg-neon-mint/20 data-[state=active]:text-neon-mint">
                    Individual Lead
                  </TabsTrigger>
                  <TabsTrigger value="capture_page" className="flex-1 data-[state=active]:bg-neon-mint/20 data-[state=active]:text-neon-mint">
                    Capture Page
                  </TabsTrigger>
                  <TabsTrigger value="all" className="flex-1 data-[state=active]:bg-neon-mint/20 data-[state=active]:text-neon-mint">
                    All Leads
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              
              {formData.recipient_type === 'individual' && (
                <Select 
                  value={formData.lead_id} 
                  onValueChange={(v) => setFormData({...formData, lead_id: v})}
                >
                  <SelectTrigger className="bg-zinc-900 border-white/20 text-white">
                    <SelectValue placeholder="Select a lead" />
                  </SelectTrigger>
                  <SelectContent className="bg-void-paper border-white/20">
                    {leads.map((lead) => (
                      <SelectItem key={lead.id} value={lead.id} className="text-white">
                        {lead.name} ({lead.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              {formData.recipient_type === 'capture_page' && (
                <Select 
                  value={formData.landing_page_id} 
                  onValueChange={(v) => setFormData({...formData, landing_page_id: v})}
                >
                  <SelectTrigger className="bg-zinc-900 border-white/20 text-white">
                    <SelectValue placeholder="Select a capture page" />
                  </SelectTrigger>
                  <SelectContent className="bg-void-paper border-white/20">
                    {pages.map((page) => (
                      <SelectItem key={page.id} value={page.id} className="text-white">
                        {page.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              {formData.recipient_type === 'all' && (
                <p className="text-gray-400 text-sm">
                  This message will be sent to all {leads.length} leads
                </p>
              )}
            </div>

            {/* Message Content */}
            <div className="space-y-4">
              <div>
                <Label className="text-gray-300">Subject</Label>
                <Input
                  value={formData.subject}
                  onChange={(e) => setFormData({...formData, subject: e.target.value})}
                  placeholder="Email subject line"
                  className="bg-zinc-900 border-white/20 text-white placeholder:text-gray-500 mt-1"
                />
              </div>
              <div>
                <Label className="text-gray-300">Message *</Label>
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  placeholder="Write your message here..."
                  rows={5}
                  className="bg-zinc-900 border-white/20 text-white placeholder:text-gray-500 mt-1"
                  required
                />
              </div>
              <div>
                <Label className="text-gray-300 flex items-center gap-2">
                  <Youtube className="w-4 h-4 text-red-500" />
                  YouTube Video
                  <span className="text-gray-500 text-xs font-normal">(optional)</span>
                </Label>
                <Input
                  type="url"
                  value={formData.youtube_url}
                  onChange={(e) => setFormData({...formData, youtube_url: e.target.value})}
                  placeholder="https://youtube.com/watch?v=..."
                  className="bg-zinc-900 border-white/20 text-white placeholder:text-gray-500 mt-1"
                  data-testid="scheduled-youtube-url"
                />
              </div>
              <div>
                <Label className="text-gray-300 flex items-center gap-2">
                  <Link2 className="w-4 h-4 text-neon-mint" />
                  Link / CTA Button
                  <span className="text-gray-500 text-xs font-normal">(optional)</span>
                </Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="url"
                    value={formData.cta_link}
                    onChange={(e) => setFormData({...formData, cta_link: e.target.value})}
                    placeholder="https://yourwebsite.com"
                    className="bg-zinc-900 border-white/20 text-white placeholder:text-gray-500 flex-1"
                    data-testid="scheduled-cta-link"
                  />
                  <Input
                    value={formData.cta_text}
                    onChange={(e) => setFormData({...formData, cta_text: e.target.value})}
                    placeholder="Button text"
                    className="bg-zinc-900 border-white/20 text-white placeholder:text-gray-500 w-36"
                    data-testid="scheduled-cta-text"
                  />
                </div>
              </div>
            </div>

            {/* Schedule Type */}
            <div className="space-y-4">
              <Label className="text-white font-medium">Schedule Type</Label>
              <Tabs 
                value={formData.schedule_type} 
                onValueChange={(v) => setFormData({...formData, schedule_type: v})}
              >
                <TabsList className="bg-zinc-900/50">
                  <TabsTrigger value="one_time" className="data-[state=active]:bg-neon-mint/20 data-[state=active]:text-neon-mint">
                    <Clock className="w-4 h-4 mr-2" />
                    One-Time
                  </TabsTrigger>
                  <TabsTrigger value="recurring" className="data-[state=active]:bg-neon-mint/20 data-[state=active]:text-neon-mint">
                    <Repeat className="w-4 h-4 mr-2" />
                    Recurring
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {/* Date/Time Selection */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-gray-300">Date *</Label>
                <Input
                  type="date"
                  value={formData.scheduled_date}
                  onChange={(e) => setFormData({...formData, scheduled_date: e.target.value})}
                  className="bg-zinc-900 border-white/20 text-white mt-1"
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>
              <div>
                <Label className="text-gray-300">Time *</Label>
                <Input
                  type="time"
                  value={formData.scheduled_time}
                  onChange={(e) => setFormData({...formData, scheduled_time: e.target.value})}
                  className="bg-zinc-900 border-white/20 text-white mt-1"
                  required
                />
              </div>
              <div>
                <Label className="text-gray-300">Timezone</Label>
                <Select 
                  value={formData.timezone} 
                  onValueChange={(v) => setFormData({...formData, timezone: v})}
                >
                  <SelectTrigger className="bg-zinc-900 border-white/20 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-void-paper border-white/20 max-h-60">
                    {timezones.map((tz) => (
                      <SelectItem key={tz} value={tz} className="text-white">
                        {tz.replace(/_/g, ' ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Recurring Options */}
            {formData.schedule_type === 'recurring' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
                <div>
                  <Label className="text-gray-300">Repeat Every</Label>
                  <Select 
                    value={formData.recurring_frequency} 
                    onValueChange={(v) => setFormData({...formData, recurring_frequency: v})}
                  >
                    <SelectTrigger className="bg-zinc-900 border-white/20 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-void-paper border-white/20">
                      <SelectItem value="daily" className="text-white">Daily</SelectItem>
                      <SelectItem value="weekly" className="text-white">Weekly</SelectItem>
                      <SelectItem value="monthly" className="text-white">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">End Date (Optional)</Label>
                  <Input
                    type="date"
                    value={formData.recurring_end_date}
                    onChange={(e) => setFormData({...formData, recurring_end_date: e.target.value})}
                    className="bg-zinc-900 border-white/20 text-white mt-1"
                    min={formData.scheduled_date || new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="border-white/20 text-white"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-gradient-to-r from-neon-mint to-neon-violet"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Clock className="w-4 h-4 mr-2" />
                )}
                {editingMessage ? 'Update Schedule' : 'Schedule Message'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="bg-void-paper border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Message Preview</DialogTitle>
          </DialogHeader>
          
          {previewLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-neon-mint" />
            </div>
          ) : previewData ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">Recipients: {previewData.recipient_count}</span>
                {previewData.sample_recipient && (
                  <span className="text-gray-400">
                    Sample: {previewData.sample_recipient.name} ({previewData.sample_recipient.email})
                  </span>
                )}
              </div>
              
              <div className="border border-white/20 rounded-lg overflow-hidden">
                <div className="bg-gray-800 px-4 py-2 text-sm text-gray-400">
                  Subject: {previewData.scheduled_message?.subject || 'No subject'}
                </div>
                <div 
                  className="bg-white p-4"
                  dangerouslySetInnerHTML={{ __html: previewData.preview_html }}
                />
              </div>
            </div>
          ) : (
            <p className="text-gray-400 text-center py-8">No preview available</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ScheduledMessages;
